import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, CircularProgress, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { api, type Tenant, type TenantMember, type TenantInvite } from '../services/api';
import { useStore } from '../store/useStore';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrator' },
  { value: 'manager', label: 'Manager' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'viewer', label: 'Viewer' },
];

export default function TenantPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useStore();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('inspector');
  const [inviteMethod, setInviteMethod] = useState('email');
  const [inviteMessage, setInviteMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const tenantData = await api.getMyTenant();
        setTenant(tenantData);
        const tenantMembers = await api.getTenantMembers(tenantData.id);
        setMembers(tenantMembers);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const refreshMembers = async () => {
    if (!tenant) return;
    const tenantMembers = await api.getTenantMembers(tenant.id);
    setMembers(tenantMembers);
  };

  const handleInvite = async () => {
    if (!tenant) return;
    if (!inviteEmail.trim()) {
      setError('Enter an email to invite.');
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      const invite: TenantInvite = await api.inviteTenantMember(
        tenant.id,
        inviteEmail.trim(),
        inviteRole,
        inviteMethod,
        inviteMessage.trim() || undefined,
      );
      setSuccess(`Invitation created for ${invite.email}. Token: ${invite.token}`);
      setInviteEmail('');
      setInviteMessage('');
      await refreshMembers();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Invite failed';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (memberId: string, userId: string, role: string) => {
    if (!tenant) return;
    try {
      await api.updateTenantMember(tenant.id, userId, role, undefined);
      await refreshMembers();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to update role');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!tenant) return;
    try {
      await api.removeTenantMember(tenant.id, userId);
      setMembers(prev => prev.filter(member => member.user_id !== userId));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to remove member');
    }
  };

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: 'text.primary', mb: 2 }}>Tenant Company</Typography>
      {loading ? (
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      ) : !tenant ? (
        <Card sx={{ borderRadius: 3, p: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` }}>
          <Typography sx={{ color: 'text.primary' }}>No tenant organization found for your account.</Typography>
        </Card>
      ) : (
        <>
          <Card sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}`, mb: 3 }}>
            <CardContent>
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>Company</Typography>
              <Typography sx={{ color: 'text.primary', fontSize: 22, fontWeight: 'bold' }}>{tenant.name}</Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>Tenant ID: {tenant.id}</Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}`, mb: 3 }}>
            <CardContent>
              <Typography sx={{ color: 'text.primary', fontWeight: 700, mb: 2 }}>Invite a team member</Typography>
              {error && <Typography sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>}
              {success && <Typography sx={{ color: 'success.main', mb: 2 }}>{success}</Typography>}

              <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                  sx={{ '& .MuiOutlinedInput-root': { color: 'text.primary' } }}
                />
                <TextField
                  select
                  fullWidth
                  label="Role"
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                  sx={{ '& .MuiOutlinedInput-root': { color: 'text.primary' } }}
                >
                  {ROLE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Invite via"
                  value={inviteMethod}
                  onChange={e => setInviteMethod(e.target.value)}
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                  sx={{ '& .MuiOutlinedInput-root': { color: 'text.primary' } }}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="text">Text message</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Message (optional)"
                  value={inviteMessage}
                  onChange={e => setInviteMessage(e.target.value)}
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                  sx={{ '& .MuiOutlinedInput-root': { color: 'text.primary' } }}
                  multiline
                  rows={3}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleInvite}
                disabled={saving}
                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Invitation'}
              </Button>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` }}>
            <CardContent>
              <Typography sx={{ color: 'text.primary', fontWeight: 700, mb: 2 }}>Team Members</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>Name</TableCell>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>Email</TableCell>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>Role</TableCell>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>Active</TableCell>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map(member => (
                    <TableRow key={member.id} sx={{ '&:hover': { bgcolor: 'background.default' } }}>
                      <TableCell sx={{ color: 'text.primary', borderColor: 'divider' }}>{member.user.full_name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>{member.user.email}</TableCell>
                      <TableCell sx={{ borderColor: 'divider' }}>
                        <TextField
                          select
                          value={member.role}
                          onChange={e => handleUpdateRole(member.id, member.user_id, e.target.value)}
                          sx={{ width: 180, '& .MuiOutlinedInput-root': { color: 'text.primary' } }}
                        >
                          {ROLE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider' }}>{member.user.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell sx={{ borderColor: 'divider' }}>
                        <IconButton onClick={() => handleRemoveMember(member.user_id)} sx={{ color: 'error.main' }}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
